import { z } from 'zod';

import { ScanWindowSchema, type ScanCandidateDto } from './marketScan';

const optionalNumber = (min: number, max: number) => z.number().min(min).max(max).optional();
const IndustryNamesSchema = z.array(z.string().trim().min(1).max(120)).max(50);

/** Filters over a session's stored stock universe; omitted bounds do not filter. */
export const ScreenFiltersSchema = z
  .object({
    window: ScanWindowSchema,
    minPerformancePct: optionalNumber(-100, 10_000),
    maxPerformancePct: optionalNumber(-100, 10_000),
    /** Keep only the top share of the filtered stocks by the window's performance. */
    topPct: optionalNumber(0.01, 1),
    minAverageDollarVolume30d: optionalNumber(0, 1_000_000_000_000),
    minAverageVolume10d: optionalNumber(0, 10_000_000_000),
    minAdrPct: optionalNumber(0, 100),
    maxAdrPct: optionalNumber(0, 100),
    minAtrExtension: optionalNumber(-50, 50),
    maxAtrExtension: optionalNumber(-50, 50),
    minClose: optionalNumber(0, 1_000_000),
    aboveSma50: z.boolean(),
    tightOnly: z.boolean(),
    excludeBiotech: z.boolean(),
    industries: IndustryNamesSchema,
    excludeIndustries: IndustryNamesSchema,
  })
  .strict();
export type ScreenFilters = z.infer<typeof ScreenFiltersSchema>;

export const DEFAULT_SCREEN_FILTERS: ScreenFilters = {
  window: '1M',
  aboveSma50: false,
  tightOnly: false,
  excludeBiotech: true,
  industries: [],
  excludeIndustries: [],
};

export const SaveScreenSchema = z
  .object({ name: z.string().trim().min(1).max(60), filters: ScreenFiltersSchema })
  .strict();
export type SaveScreenInput = z.infer<typeof SaveScreenSchema>;

export const RunScreenSchema = z.object({ filters: ScreenFiltersSchema }).strict();

export interface MarketScreenDto {
  id: string;
  name: string;
  filters: ScreenFilters;
  updatedAt: string;
}

/** Most rows a screen returns; the total still counts every match. */
export const SCREEN_RESULT_LIMIT = 500;

export interface ScreenResultDto {
  status: 'READY' | 'NOT_AVAILABLE';
  sessionDate: string | null;
  total: number;
  /** Stocks in the universe whose coil was checked; tight-only screens only see these. */
  coilChecked: number;
  stocks: ScanCandidateDto[];
  /** Industries present in the universe, for the filter pickers. */
  industries: string[];
}
